import React from "react";
import { ArrowLeft } from "react-feather";
import styled, { css } from "styled-components";
import Banner from "./Banner";
import Button from "./Button";
import useDocumentTitle from "./hooks/useDocumentTitle";

interface Props {
  children?: React.ReactNode;
  previousTitle?: string;
  previousRoute?: string;
  title: React.ReactNode;
  titleChildren?: React.ReactNode;
  error?: string;
  dirty?: boolean;
  success?: boolean | string;
  noPadding?: boolean;
  documentTitle?: string;
}

const StyledPageTitle = styled.div<{ $banner: boolean; $noPadding?: boolean }>`
  padding: 5px 20px;
  color: var(--gray400);
  position: relative;
  display: flex;
  align-items: flex-end;

  ${(props) =>
    props.$noPadding &&
    css`
      padding-left: 0;
      padding-right: 0;
      padding-top: 0;
    `}

  ${(props) =>
    props.$banner &&
    css`
      padding-top: 50px;

      @media (min-width: 1030px) {
        padding-top: 5px;
      }
    `}
`;

const Buttons = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  justify-content: flex-end;

  > * {
    margin-bottom: 5px;
  }

  > * + * {
    margin-left: 10px;
  }

  > button,
  > a {
    margin-bottom: 10px;
  }
`;

const Title = styled.div`
  margin-right: auto;
  display: flex;
  min-height: 45px;
  align-items: center;

  span {
    font-weight: 500;
    font-size: 24px;
    font-weight: 500;
  }
`;

const StyledBanner = styled(Banner)`
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 10px;

  @media (min-width: 1030px) {
    position: static;
    transform: none;
    margin: 0;
    margin-left: 15px;
  }
`;

const BackButton = styled(Button)`
  margin-right: 5px;
  margin-bottom: 5px;
`;

const PageTitle = (props: Props) => {
  const {
    previousTitle,
    title,
    documentTitle,
    children,
    previousRoute,
    error,
    dirty,
    success,
    titleChildren,
    noPadding,
  } = props;

  const showBanner = !!(error || dirty || success);

  useDocumentTitle(documentTitle);

  return (
    <StyledPageTitle $banner={showBanner} $noPadding={noPadding}>
      {previousTitle && previousRoute && (
        <BackButton
          icon={ArrowLeft}
          iconSize={19}
          tooltip={previousTitle}
          link={previousRoute}
        />
      )}
      <Title>
        <span>{title}</span>
        {showBanner && (
          <StyledBanner error={error} warn={dirty} success={success} />
        )}
        {titleChildren}
      </Title>
      <Buttons>{children}</Buttons>
    </StyledPageTitle>
  );
};

export default PageTitle;
